import type { PayPeriodRow } from './payPeriodGridFigures'

/**
 * The pay period schedule laid out as a calendar: for each date, how many
 * officers work, are off, are on leave, and are away on a detail or
 * unavailable, with who is on leave or away. Pure, so the screen and its
 * test read one result.
 */
export type CalendarAbsence = { employee: string; label: string; kind: 'leave' | 'away' }

export type CalendarDay = {
  date: string
  working: number
  off: number
  leave: number
  other: number
  absences: CalendarAbsence[]
  // The one officer's own label for the day, when the calendar shows only them.
  own?: string
}

const WORKED = new Set(['work', 'training'])

export function buildCalendar(rows: PayPeriodRow[], dates: string[]): CalendarDay[] {
  return dates.map((date, index) => {
    const day: CalendarDay = { date, working: 0, off: 0, leave: 0, other: 0, absences: [] }
    for (const row of rows) {
      const cell = row.cells[index]
      if (!cell) continue
      if (WORKED.has(cell.category)) day.working += 1
      else if (cell.category === 'off') day.off += 1
      else if (cell.category === 'leave') {
        day.leave += 1
        day.absences.push({ employee: row.employee, label: cell.label, kind: 'leave' })
      } else {
        day.other += 1
        day.absences.push({ employee: row.employee, label: cell.label, kind: 'away' })
      }
    }
    day.absences.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'leave' ? -1 : 1))
    if (rows.length === 1) day.own = rows[0].cells[index]?.label
    return day
  })
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const parts = (iso: string) => iso.split('-').map(Number) as [number, number, number]
export const shiftDate = (iso: string, days: number) => {
  const [year, month, day] = parts(iso)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}
const weekdayOf = (iso: string) => {
  const [year, month, day] = parts(iso)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

// The days in Sunday to Saturday weeks; a date outside the period is a blank.
export function weeksOf(days: CalendarDay[]): (CalendarDay | { date: string; outside: true })[][] {
  if (days.length === 0) return []
  const first = days[0].date
  const last = days[days.length - 1].date
  const byDate = new Map(days.map((day) => [day.date, day]))
  const weeks: (CalendarDay | { date: string; outside: true })[][] = []
  for (let start = shiftDate(first, -weekdayOf(first)); start <= last; start = shiftDate(start, 7)) {
    weeks.push(
      Array.from({ length: 7 }, (_, index) => {
        const date = shiftDate(start, index)
        return byDate.get(date) ?? { date, outside: true as const }
      }),
    )
  }
  return weeks
}
